import experiment from "amplitude-experiment";
import isMatch from "date-fns/isMatch";
import isWithinInterval from "date-fns/isWithinInterval";
import { BannerConfigs } from "./DrxBanner.config";
import {
  AmplitudeFlagCondition,
  BannerFlagPayload,
  BannerOverride,
  Brand,
  DrxBannerConfig,
  ResolvedBannerConfig,
} from "./types";

export function getActiveBanners(): ResolvedBannerConfig[] {
  let now = new Date();
  const currentPath = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const override = params.get("banner-date-override");

  if (override && isMatch(override, "yyyy-MM-dd'T'HH:mm")) {
    now = new Date(override);
  }

  return BannerConfigs.filter(
    (config) =>
      isWithinInterval(now, { start: config.startDate, end: config.endDate }) &&
      isBannerActiveForBrand(config.activeForBrands) &&
      isBannerActiveForRoute(config.activeOnRoutes, currentPath) &&
      isBannerActiveForAmplitudeFlag(config.amplitudeFlag)
  ).map((config) => resolveBannerConfig(config, now));
}

function resolveBannerConfig(config: DrxBannerConfig, now: Date): ResolvedBannerConfig {
  const overrideMessage = getActiveOverrideMessage(config.amplitudeFlag, now);

  const text = overrideMessage ?? (typeof config.data.text === "function" ? config.data.text(now) : config.data.text);

  return {
    id: config.id,
    data: {
      text,
      type: config.data.type,
    },
  };
}

/**
 * Checks the Amplitude flag payload for an active override message.
 * Returns the override message if one is active, otherwise undefined.
 */
function getActiveOverrideMessage(flagCondition: AmplitudeFlagCondition | undefined, now: Date): string | undefined {
  if (!flagCondition) {
    return undefined;
  }

  const variant = experiment.variant(flagCondition.name);
  const payload = variant.payload as BannerFlagPayload | undefined;

  if (!payload?.overrides || !Array.isArray(payload.overrides)) {
    return undefined;
  }

  const activeOverride = payload.overrides.find((override: BannerOverride) => {
    try {
      const start = new Date(override.start);
      const end = new Date(override.end);
      return isWithinInterval(now, { start, end });
    } catch {
      return false;
    }
  });

  return activeOverride?.message;
}

function isBannerActiveForBrand(brands: Array<Brand>): boolean {
  return !!process.env.APP_THEME && brands.includes(process.env.APP_THEME as Brand);
}

function isBannerActiveForRoute(routePatterns: Array<string> | undefined, currentPath: string): boolean {
  if (!routePatterns || routePatterns.length === 0) {
    return true;
  }

  return routePatterns.some((pattern) => {
    const urlPattern = new URLPattern({ pathname: pattern });
    return urlPattern.test({ pathname: currentPath });
  });
}

function isBannerActiveForAmplitudeFlag(flagCondition: AmplitudeFlagCondition | undefined): boolean {
  if (!flagCondition) {
    return true;
  }

  const variant = experiment.variant(flagCondition.name);
  const flagValue = variant.value;

  return flagValue === flagCondition.value;
}

export { default } from "./DrxBanner.vue";
